import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MENU_GROUP_ORDER } from '../../config/menuItems';

export const DRAWER_WIDTH = 260;
export const DRAWER_WIDTH_COLLAPSED = 68;

const Sidebar = ({
  isOpen,
  onClose,
  menuItems = [],
  brandLink = '/',
  footer = null,
  collapsed = false,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Group items preserving MENU_GROUP_ORDER; ungrouped items fall into a trailing group.
  const groups = MENU_GROUP_ORDER
    .map((group) => ({ group, items: menuItems.filter((i) => i.group === group) }))
    .filter((g) => g.items.length > 0);
  const ungrouped = menuItems.filter((i) => !i.group || !MENU_GROUP_ORDER.includes(i.group));
  if (ungrouped.length > 0) groups.push({ group: null, items: ungrouped });

  // Collapse to icon rail only applies on desktop; mobile drawer is always full width.
  const railCollapsed = collapsed && isDesktop;

  const renderNavItem = (item) => {
    const active = isActiveRoute(item.path);
    const button = (
      <ListItemButton
        component={RouterLink}
        to={item.path}
        onClick={!isDesktop ? onClose : undefined}
        selected={active}
        sx={{
          position: 'relative',
          justifyContent: railCollapsed ? 'center' : 'flex-start',
          px: railCollapsed ? 1 : 1.25,
          py: 0.75,
          // Accent indicator bar for the active route
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -8,
            top: '20%',
            bottom: '20%',
            width: 3,
            borderRadius: 2,
            bgcolor: active ? 'secondary.main' : 'transparent',
            transition: 'background-color .15s ease',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: railCollapsed ? 0 : 32, justifyContent: 'center' }}>
          <item.icon fontSize="small" sx={{ color: active ? 'primary.main' : 'text.secondary' }} />
        </ListItemIcon>
        {!railCollapsed && (
          <ListItemText
            primary={item.text}
            slotProps={{
              primary: {
                sx: { fontWeight: active ? 600 : 500, color: active ? 'text.primary' : 'text.secondary' },
              },
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
        {railCollapsed ? (
          <Tooltip title={item.text} placement="right">
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Brand header */}
      <Toolbar sx={{ justifyContent: railCollapsed ? 'center' : 'space-between', px: railCollapsed ? 1 : 2 }}>
        <Box component={RouterLink} to={brandLink} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', minWidth: 0 }}>
          {railCollapsed ? (
            <Box component="img" src="/assets/pp-favicon.png" alt="PRO Portal" sx={{ height: 30, objectFit: 'contain' }} />
          ) : (
            <Box component="img" src="/assets/UFF_Logo_Main_2026.png" alt="United Fidelity Funding" sx={{ maxHeight: 40, objectFit: 'contain' }} />
          )}
        </Box>
        {!isDesktop && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>

      {/* Navigation groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, pt: 0.5 }}>
        {groups.map(({ group, items }, gi) => (
          <Box key={group ?? `group-${gi}`} sx={{ mb: 0.75 }}>
            {group && !railCollapsed && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: gi === 0 ? 0.5 : 1.25,
                  pb: 0.5,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  opacity: 0.75,
                }}
              >
                {group}
              </Typography>
            )}
            {group && railCollapsed && gi > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', mx: 1, my: 1 }} />
            )}
            <List disablePadding>{items.map(renderNavItem)}</List>
          </Box>
        ))}
      </Box>

      {/* Collapse toggle (desktop only) */}
      {isDesktop && onToggleCollapse && (
        <Box sx={{ px: 1.5, pb: 0.5, display: 'flex', justifyContent: railCollapsed ? 'center' : 'flex-end' }}>
          <Tooltip title={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
              {railCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Footer slot — global actions/user controls live here so the AppBar can be used for page context. */}
      {footer && !railCollapsed && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1.5 }}>
          {footer}
        </Box>
      )}
    </Box>
  );

  const width = railCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <>
      {/* Permanent drawer for desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            transition: 'width .2s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Temporary drawer for mobile */}
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
